import {FC} from "react";
import {
	GridToolbarContainer,
	GridToolbarColumnsButton,
	GridToolbarFilterButton,
	GridToolbarDensitySelector,
	GridToolbarExport
} from "@mui/x-data-grid-pro";
import {Button, Tooltip, Box} from "@mui/material";
import {Add, Undo, Check, EventNote} from "@mui/icons-material";
import useDevCycleSdk from "@/app2/shared/hooks/useDevCycleSdk";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {makeStyles} from "tss-react/mui";

const useStyles = makeStyles()((_theme) => ({
	customToolbar: {
		display: "flex",
		gap: "10px",
		border: "0px solid"
	},
	gap: {
		flexGrow: 100
	}
}));

interface CourseEnrollmentsGridToolbarProps {
	selectedRows: number[];
	isCoursePublished: boolean;
	handleEnroll: (userIds: number[]) => void;
	handleUnenroll: (userIds: number[]) => void;
	handleMarkComplete: (userIds: number[]) => void;
	handleManageDueDates: (userIds: number[]) => void;
}

export const CourseEnrollmentsGridToolbar: FC<CourseEnrollmentsGridToolbarProps> = ({
	selectedRows,
	isCoursePublished,
	handleEnroll,
	handleUnenroll,
	handleMarkComplete,
	handleManageDueDates
}) => {
	const {t} = useSSTranslation();
	const {classes} = useStyles();
	const {dueDateAssignments} = useDevCycleSdk();
	const hasSelectedRows = selectedRows.length > 0;

	return (
		<GridToolbarContainer className={classes.customToolbar}>
			<GridToolbarColumnsButton
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
			<GridToolbarFilterButton {...({} as any)} />
			<GridToolbarDensitySelector
				placeholder={undefined}
				onPointerEnterCapture={undefined}
				onPointerLeaveCapture={undefined}
				onResize={undefined}
				onResizeCapture={undefined}
			/>
			<GridToolbarExport />
			<Box className={classes.gap} />
			<Tooltip
				title={
					!isCoursePublished
						? t(
							"course_draft_not_enrollment_description",
							"Please publish the course to enable user enrollments.",
						  )
						: ""
				}
			>
				<span>
					<Button
						disabled={!hasSelectedRows || !isCoursePublished}
						onClick={() => handleEnroll(selectedRows)}
						data-test-id="course-enrollment-bulk-enroll-button"
						aria-label="Enroll selected employees in course"
					>
						<Add />
						{t("common_enroll", "ENROLL")}
					</Button>
				</span>
			</Tooltip>
			<Tooltip
				title={
					!isCoursePublished
						? t(
							"course_draft_not_enrollment_description",
							"Please publish the course to enable user enrollments.",
						  )
						: ""
				}
			>
				<span>
					<Button
						disabled={!hasSelectedRows || !isCoursePublished}
						onClick={() => handleUnenroll(selectedRows)}
						data-test-id="course-enrollment-bulk-unenroll-button"
						aria-label="Unenroll selected employees from course"
					>
						<Undo />
						{t("common_unenroll", "UNENROLL")}
					</Button>
				</span>
			</Tooltip>
			<Tooltip
				title={
					!isCoursePublished
						? t(
							"course_draft_not_enrollment_description",
							"Please publish the course to enable user enrollments.",
						  )
						: ""
				}
			>
				<span>
					<Button
						disabled={!hasSelectedRows || !isCoursePublished}
						onClick={() => handleMarkComplete(selectedRows)}
						data-test-id="course-enrollment-bulk-complete-button"
						aria-label="Mark selected employees as complete for course"
					>
						<Check />
						{t("common_complete", "COMPLETE")}
					</Button>
				</span>
			</Tooltip>
			{dueDateAssignments && (
				<Tooltip
					title={
						!isCoursePublished
							? t(
								"course_draft_not_enrollment_description",
								"Please publish the course to enable user enrollments.",
							  )
							: ""
					}
				>
					<span>
						<Button
							disabled={!hasSelectedRows || !isCoursePublished}
							onClick={() => handleManageDueDates(selectedRows)}
							data-test-id="course-enrollment-manage-due-dates-button"
							aria-label="Manage due dates for selected employees"
						>
							<EventNote />
							{t("manage_due_dates_toolbar", "MANAGE DUE DATES")}
						</Button>
					</span>
				</Tooltip>
			)}
		</GridToolbarContainer>
	);
};
